import React, { useCallback, useMemo } from 'react'
import { useSiteContext } from '@contexts/MkSiteContext'
import { useAuthLabels } from '@account/hooks/useAuthLabels'
import { getAuthLabel as getAuthLabelUtil, type PageContentResponse } from '@account/services/authLabels'
import { VALIDATION_MESSAGES } from '@constants'
import { createLogger } from '@utils/Logger'

const logger = createLogger('AuthLabels')

export type GetLabelFn = (flow: string, step: string, path: string, fallback: string) => string

/** Map field name to backend validation key and fallback message (flows.validation.validation) */
const FIELD_TO_VALIDATION: Record<string, { backendKey: string; fallback: string }> = {
  ownerNumber: { backendKey: 'ownerNumberRequired', fallback: VALIDATION_MESSAGES.OWNER_NUMBER_REQUIRED },
  firstName: { backendKey: 'firstNameRequired', fallback: VALIDATION_MESSAGES.FIRST_NAME_REQUIRED },
  lastName: { backendKey: 'lastNameRequired', fallback: VALIDATION_MESSAGES.LAST_NAME_REQUIRED },
  companyName: { backendKey: 'companyNameRequired', fallback: VALIDATION_MESSAGES.COMPANY_NAME_REQUIRED },
  verificationMethod: { backendKey: 'verificationMethodRequired', fallback: VALIDATION_MESSAGES.VERIFICATION_METHOD_REQUIRED },
  phone: { backendKey: 'phoneRequired', fallback: VALIDATION_MESSAGES.PHONE_REQUIRED },
  phoneNumber: { backendKey: 'phoneRequired', fallback: VALIDATION_MESSAGES.PHONE_REQUIRED },
  contractNumber: { backendKey: 'contractNumberRequired', fallback: VALIDATION_MESSAGES.CONTRACT_NUMBER_REQUIRED },
  email: { backendKey: 'emailRequired', fallback: VALIDATION_MESSAGES.EMAIL_REQUIRED },
  emailAddress: { backendKey: 'emailRequired', fallback: VALIDATION_MESSAGES.EMAIL_REQUIRED },
  userName: { backendKey: 'usernameInvalid', fallback: VALIDATION_MESSAGES.USERNAME_INVALID },
  username: { backendKey: 'usernameInvalid', fallback: VALIDATION_MESSAGES.USERNAME_INVALID },
  password: { backendKey: 'passwordInvalid', fallback: VALIDATION_MESSAGES.PASSWORD_INVALID },
  passwordRequired: { backendKey: 'passwordRequired', fallback: VALIDATION_MESSAGES.PASSWORD_REQUIRED },
  newPassword: { backendKey: 'passwordInvalid', fallback: VALIDATION_MESSAGES.PASSWORD_INVALID },
  confirmPassword: { backendKey: 'passwordsDoNotMatch', fallback: VALIDATION_MESSAGES.PASSWORDS_DO_NOT_MATCH },
  verificationCode: { backendKey: 'verificationCodeRequired', fallback: VALIDATION_MESSAGES.VERIFICATION_CODE_REQUIRED },
  postalCode: { backendKey: 'postalCodeRequired', fallback: VALIDATION_MESSAGES.POSTAL_CODE_REQUIRED },
}

export type GetValidationMessageFn = (fieldName: string) => string

export interface AuthLabelsContextType {
  getLabel: GetLabelFn
  getValidationMessage: GetValidationMessageFn
  flows: PageContentResponse['flows'] | null
  loading: boolean
  error: Error | null
}

const defaultGetLabel: GetLabelFn = (_, __, ___, fallback) => fallback
const defaultGetValidationMessage: GetValidationMessageFn = (fieldName) => {
  const config = FIELD_TO_VALIDATION[fieldName]
  if (config) return config.fallback
  if (process.env.NODE_ENV === 'development') {
    logger.warn(`Missing validation mapping for field "${fieldName}" in AuthLabelsContext`)
  }
  return ''
}

export const AuthLabelsContext = React.createContext<AuthLabelsContextType>({
  getLabel: defaultGetLabel,
  getValidationMessage: defaultGetValidationMessage,
  flows: null,
  loading: false,
  error: null,
})

export const AuthLabelsProvider = ({ children }: { children: React.ReactNode }) => {
  const { themeBrand } = useSiteContext()
  const { data, loading, error } = useAuthLabels(themeBrand)
  const flows = data?.flows ?? null

  const getLabel = useCallback<GetLabelFn>((flow, step, path, fallback) => getAuthLabelUtil(flows ?? undefined, flow, step, path, fallback), [flows])

  const getValidationMessage = useCallback<GetValidationMessageFn>(
    (fieldName) => {
      const config = FIELD_TO_VALIDATION[fieldName]
      if (!config) {
        if (process.env.NODE_ENV === 'development') {
          logger.warn(`Missing validation mapping for field "${fieldName}" in AuthLabelsContext`)
        }
        return ''
      }
      return getAuthLabelUtil(flows ?? undefined, 'validation', 'validation', config.backendKey, config.fallback)
    },
    [flows],
  )

  const value: AuthLabelsContextType = useMemo(
    () => ({
      getLabel,
      getValidationMessage,
      flows,
      loading,
      error,
    }),
    [getLabel, getValidationMessage, flows, loading, error],
  )

  return <AuthLabelsContext.Provider value={value}>{children}</AuthLabelsContext.Provider>
}

export function useAuthLabelsContext(): AuthLabelsContextType {
  const context = React.useContext(AuthLabelsContext)
  return context
}
